/**
 * SRECTrade API Client Configuration
 *
 * This module provides the base configuration for the SRECTrade API client,
 * including environment-specific settings and authentication handling.
 */

import { browser } from '$app/environment';
import { createClient, createConfig } from './generated/client/index.js';
import type { Client } from './generated/client/types.gen.js';
import { api } from '$lib/config/env';
import { isNativeApp } from '$lib/capacitor';
import { getScenario } from '$lib/mocks/scenarios';

// Track if we're currently handling a 401 to prevent multiple simultaneous refresh attempts
let handlingUnauthorized = false;
// Track if we're currently refreshing to avoid duplicate refresh calls
let refreshPromise: Promise<boolean> | null = null;

/** Environment configuration interface */
interface ApiEnvironment {
	/** Base URL for the API */
	baseUrl: string;
	/** Whether this is a development environment */
	isDevelopment: boolean;
	/** Whether to use mock data */
	useMocks: boolean;
}

/** Authentication token management */
interface AuthConfig {
	/** API key for static authentication */
	apiKey?: string;
	/** JWT token for user authentication */
	jwtToken?: string;
}

/** API client configuration */
interface ApiClientConfig {
	environment: ApiEnvironment;
	auth?: AuthConfig;
	/** Request timeout in milliseconds */
	timeout?: number;
	/** Whether to enable detailed logging */
	enableLogging?: boolean;
}

/**
 * Determines the current environment configuration
 * @returns Environment configuration object
 */
function getEnvironmentConfig(): ApiEnvironment {
	return {
		baseUrl: api.baseUrl,
		isDevelopment: api.baseUrl.includes('localhost') || api.baseUrl.includes('127.0.0.1'),
		useMocks: api.useMocks
	};
}

/**
 * Creates and configures the API client instance
 * @param config - Optional client configuration
 * @returns Configured API client
 */
export function createApiClient(config?: Partial<ApiClientConfig>): Client {
	const environment = getEnvironmentConfig();
	const finalConfig: ApiClientConfig = {
		environment,
		timeout: api.timeout,
		enableLogging: environment.isDevelopment,
		...config
	};

	// Build base headers
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		Accept: 'application/json'
	};

	// Add authentication if available
	if (finalConfig.auth?.jwtToken) {
		headers.Authorization = `Bearer ${finalConfig.auth.jwtToken}`;
	} else if (finalConfig.auth?.apiKey) {
		headers.Authorization = `Bearer ${finalConfig.auth.apiKey}`;
	}

	if (isNativeApp()) {
		headers['X-Client-Type'] = 'mobile';
	}

	// Add Prefer header for test scenario selection (dev/mock mode only)
	if (finalConfig.environment.useMocks && typeof window !== 'undefined') {
		const scenario = getScenario();
		if (scenario) {
			headers.Prefer = `example=${scenario}`;
			console.log(`[API Client] Using test scenario: ${scenario}, adding Prefer header`);
		} else {
			console.log('[API Client] No scenario set in sessionStorage');
		}
	}

	// CapacitorHttp strips response headers, so force JSON parsing on native.
	const isNative = isNativeApp();
	const parseAs = isNative ? ('json' as const) : ('auto' as const);
	const clientConfig = createConfig({
		baseUrl: finalConfig.environment.baseUrl,
		headers,
		parseAs
	});

	// Create the client with the configuration
	const client = createClient(clientConfig);

	// Add request interceptor for auth_401 scenario to force 401 on readings POST
	if (finalConfig.environment.useMocks) {
		client.interceptors.request.use((request) => {
			const scenario = getScenario();
			if (scenario === 'auth_401' && request.method === 'POST') {
				const url = request.url;
				// Check if this is a readings submission endpoint
				if (url.includes('/readings') && !url.includes('/image-readings')) {
					request.headers.set('Prefer', 'example=auth_401, code=401');
					console.log('[API Client] auth_401 scenario: forcing 401 on readings POST');
				}
			}
			return request;
		});
	}

	if (isNative) {
		client.interceptors.response.use(async (response) => {
			if (response.ok && !response.bodyUsed) {
				const text = await response.text();
				return new Response(text, {
					status: response.status,
					statusText: response.statusText,
					headers: { 'Content-Type': 'application/json' }
				});
			}
			return response;
		});
	}

	// Add response interceptor to handle 401 Unauthorized errors
	client.interceptors.response.use((response) => {
		if (browser && response.status === 401 && !handlingUnauthorized) {
			handlingUnauthorized = true;
			console.log('[API Client] Received 401 Unauthorized - attempting token refresh...');

			// Try to refresh token before logging out
			const attemptRefresh = async (): Promise<boolean> => {
				try {
					const { refreshToken } = await import('$lib/auth/token-refresh');
					const { authActions, authGetters } = await import('$lib/stores/auth');

					const currentToken = authGetters.getToken();
					if (!currentToken) {
						console.log('[API Client] No current token, skipping refresh');
						return false;
					}

					console.log('[API Client] Calling refreshToken...');
					const result = await refreshToken(currentToken);

					if (result.success && result.token) {
						console.log('[API Client] Token refresh successful, updating auth store');
						authActions.updateToken(result.token, result.expiresAt);
						return true;
					}

					console.log('[API Client] Token refresh failed:', result.error);
					return false;
				} catch (error) {
					console.error('[API Client] Token refresh exception:', error);
					return false;
				}
			};

			// Use shared promise to avoid duplicate refresh calls
			if (!refreshPromise) {
				refreshPromise = attemptRefresh();
			}

			refreshPromise.then(async (refreshSuccess) => {
				refreshPromise = null;

				if (refreshSuccess) {
					console.log('[API Client] Token refreshed - user can retry their action');
					handlingUnauthorized = false;
					// Note: The original request already failed, but subsequent requests will use the new token
					// TanStack Query's retry mechanism or user action will trigger a new request
				} else {
					console.log('[API Client] Token refresh failed - logging out...');
					// Dynamic import to avoid circular dependency
					const { authService } = await import('$lib/auth/service');
					try {
						const redirectUrl = await authService.logout();
						if (redirectUrl) {
							console.log('[API Client] Logout complete, redirecting to:', redirectUrl);
							window.location.href = redirectUrl;
						} else {
							// Native: local cleanup done, navigate to login
							console.log('[API Client] Native logout complete, navigating to login');
							const { goto } = await import('$app/navigation');
							goto('/');
						}
					} catch (logoutError) {
						console.error('[API Client] Logout failed:', logoutError);
						window.location.href = '/';
					} finally {
						setTimeout(() => {
							handlingUnauthorized = false;
						}, 1000);
					}
				}
			});
		}
		return response;
	});

	return client;
}

/**
 * Default API client instance
 * This can be used throughout the application for standard API calls
 */
export const apiClient = createApiClient();

/**
 * Creates a new API client instance with authentication
 * @param auth - Authentication configuration
 * @returns Configured API client with authentication
 */
export function createAuthenticatedClient(auth: AuthConfig): Client {
	return createApiClient({ auth });
}

/**
 * Environment utilities
 */
export const environment = {
	/** Get current environment configuration */
	get config(): ApiEnvironment {
		return getEnvironmentConfig();
	},

	/** Check if currently in development mode */
	get isDevelopment(): boolean {
		return getEnvironmentConfig().isDevelopment;
	},

	/** Check if mocks are enabled */
	get useMocks(): boolean {
		return getEnvironmentConfig().useMocks;
	}
};

// Export types for use in other modules
export type { ApiEnvironment, AuthConfig, ApiClientConfig };

// Re-export generated client types
export type { Client, Config, ClientOptions } from './generated/client/types.gen.js';
